import { EnumCacheEntityType } from "@/app/services/generated/enums/enumCacheEntityType.generated";
import {
  EntityWithGuidKeyCache,
  EntityCacheStore,
} from "@/app/caching/apiCache";
import type { Item } from "@/app/services/generated/itemService";
import { itemService } from "@/app/services/generated/itemService";

export interface IHasItemsCache {
  items: ItemsCache;
}

// Singleton cache store
const itemCacheStore = new EntityCacheStore<Item>(
  EnumCacheEntityType.item,
  async () => itemService.getAllItems()
);

// Ephemeral reader
export default class ItemsCache extends EntityWithGuidKeyCache<Item> {
  _depositCharges: readonly Item[] = [];
  _depositRedemptionItem: Item | null = null;

  constructor() {
    super(itemCacheStore, (x) => x.details.id);
  }

  async update(): Promise<void> {
    await super.update();
    this._depositRedemptionItem =
      this.all.find((x) => x.details.mainPurpose.isBottleDepositRedemption()) ??
      null;

    this._depositCharges = this.all.filter((x) =>
      x.details.mainPurpose.isBottleDepositCharge()
    );
  }

  onlyBatchTracked() {
    return this.allReactive.value.filter(
      (item) => item.stock?.isBatchTracked ?? false
    );
  }

  products() {
    return this.allReactive.value.filter((item) =>
      item.details.mainPurpose.isProduct()
    );
  }

  productsAndAutoAssembled() {
    return this.all.filter(
      (item) =>
        item.details.mainPurpose.isProduct() ||
        item.details.mainPurpose.isAutoAssembledItem()
    );
  }

  hasBatchTrackedProducts() {
    return this.all.some(
      (item) =>
        item.details.mainPurpose.isProduct() && item.stock?.isBatchTracked
    );
  }

  deliveryServices() {
    return this.allReactive.value.filter((item) =>
      item.details.mainPurpose.isDeliveryService()
    );
  }

  get depositCharges() {
    return this._depositCharges;
  }

  get depositRedemptionItem() {
    return this._depositRedemptionItem;
  }

  get depositRedemptionItemId() {
    return this._depositRedemptionItem?.details.id ?? null;
  }
}
